import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { subscribeNotifications, markNotificationsRead } from '../firebase/likeService';
import { useChatUI } from '../context/ChatUIContext';
import { USER_TYPES } from '../constants/designer';
import './NotificationBell.css';
import PersonIcon from './PersonIcon';

// What the row SAYS. `type` is absent on notifications written before item
// likes existed; those were all profile likes, so that is the default.
function messageOf(n, t) {
  const who = n.fromName || '—';
  if (n.type === 'project_like') return t('notif_liked_project').replace('{name}', who);
  if (n.type === 'photo_like') return t('notif_liked_photo').replace('{name}', who);
  if (n.type === 'comment_reply') return t('notif_replied').replace('{name}', who);
  // Somebody commented on YOUR work. Says which kind it was, because a
  // designer has both and "commented on your photo" under a 3D scene is wrong.
  if (n.type === 'item_comment') {
    const key = n.itemKind === 'design' ? 'notif_commented_design' : 'notif_commented_photo';
    return t(key).replace('{name}', who);
  }
  return t('notif_liked').replace('{name}', who);
}

// Where the row GOES, or null for a row that isn't a link.
//
// ONLY a like on a piece of WORK is clickable, and it opens exactly the thing
// that was liked — on YOUR OWN DASHBOARD, never on the public profile.
//
// It used to open /designer/<your own uid>, which is the page strangers see of
// you. That put the owner in front of a Like button for their own profile and
// their own photos: an action that should never have been offered, and which
// the rules now refuse outright. Your work belongs on your page.
//
// A "liked your profile" notification stays unlinked: the only place it could
// lead is back to the liker, which is not what the row is about, and for a
// customer that page doesn't exist at all.
// YOUR PROFILE, not the dashboard. The gallery these open used to sit on the
// dashboard, so that is where they pointed. It now lives on /profile, and on a
// phone the dashboard is the FEED — landing there made the feed grow a copy of
// your own posted work just so the notification had something to open, which is
// not what a feed is. The profile is where that work belongs on both layouts.
function destinationOf(n, meUid) {
  if (!n.itemId) return null;
  if (n.type === 'project_like') return `/profile?design=${n.itemId}`;
  if (n.type === 'photo_like') return `/profile?photo=${n.itemId}`;

  // WHICH GALLERY the comment was left in. Absent on rows written before the
  // field existed, and every one of those was a photo. It matters because
  // `?photo=<designId>` is looked up in the photo list, finds nothing, and
  // opens no viewer at all — the link appeared to work and did nothing.
  const param = n.itemKind === 'design' ? 'design' : 'photo';
  // `?comment=` scrolls the thread to the line the notification is about
  // rather than dropping the reader at the top of it (see Comments.jsx).
  const at = n.commentId ? `&comment=${n.commentId}` : '';

  // YOUR OWN WORK opens on YOUR OWN page, the way the like rows do — never on
  // the public profile, which is the version strangers see and which offers
  // the owner a Like button for their own photo.
  const mine = !!n.ownerUid && n.ownerUid === meUid;
  if (n.type === 'item_comment') return `/profile?${param}=${n.itemId}${at}`;
  // A reply is usually on SOMEBODY ELSE'S item, which /profile cannot show —
  // it holds only your own work — so it goes through the owner's public page.
  // Unless the owner is you: someone answering your comment under your own
  // picture is still your picture.
  if (n.type === 'comment_reply') {
    if (mine) return `/profile?${param}=${n.itemId}${at}`;
    return n.ownerUid ? `/designer/${n.ownerUid}?${param}=${n.itemId}${at}` : null;
  }
  return null;
}

// ── NOTIFICATION BELL ────────────────────────────────────────────────────────
// Unread count on the icon, list in a dropdown. Notifications are written by a
// Firestore trigger when someone likes you (functions/onLikeWrite.js), never by
// a client — so "X liked you" can't be forged.
//
// LIVE, not fetched once. This was a fetch on mount plus another on open, on
// the reasoning that a like is not time-critical — but a badge that only
// updates when you reload is not telling you anything you did not already go
// looking for. One listener per signed-in designer, on a query already capped
// at 30 rows, and it replaces the two fetches rather than adding to them.
export default function NotificationBell({ variant = 'icon', label } = {}) {
  const { user, profile } = useAuth();
  const { t } = useT();
  const { close: closeMessages } = useChatUI();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  // The tab-bar panel is portalled out of the bar, so it is NOT inside
  // wrapRef — without its own ref every click on a notification would count as
  // "outside" and close the panel before the link could fire.
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user?.uid) { setItems([]); return undefined; }
    // REQUIRED: without this the listener outlives the component. The bell is
    // mounted on every page and twice over on a phone (header + tab bar), so a
    // leak here would accumulate a subscription per navigation.
    return subscribeNotifications(user.uid, setItems);
  }, [user?.uid]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const inButton = wrapRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = items.filter(n => !n.read);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (!next) return;
    // No refetch on open — the listener has already delivered whatever exists.
    // Marked read on OPEN, not on click-through: opening the panel is the act
    // of seeing them. Optimistic locally so the badge clears immediately; the
    // snapshot that follows the write confirms it.
    const ids = items.filter(n => !n.read).map(n => n.id);
    if (ids.length) {
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      markNotificationsRead(ids).catch(() => {});
    }
  }

  // EVERY SIGNED-IN ACCOUNT NOW. This used to be designers only, on the
  // reasoning that liking was the only thing that produced a notification and
  // only designers can be liked. Comment replies broke that: a customer who
  // comments on a photo can be replied to, and had nowhere to be told.
  if (!user?.uid) return null;

  // A HEART FOR ACCOUNTS THAT CAN BE LIKED, A BELL FOR EVERYONE ELSE.
  //
  // A designer's or a shop's panel is overwhelmingly "someone liked your work",
  // and the heart says that better than a bell does. A customer's can only ever
  // be a reply, so a heart there would be a promise of something they never get.
  const isDesigner = profile?.userType === USER_TYPES.DESIGNER
                  || profile?.userType === USER_TYPES.SHOP;

  // `tab` is the same bell rendered as a bottom-bar tab (see MobileTabBar).
  // A variant rather than a second component: the fetching, the mark-read-on-
  // open and the badge are the whole of this thing, and a copy of them in the
  // tab bar would be a copy to keep in step.
  const tab = variant === 'tab';

  // THE TAB PANEL IS PORTALLED OUT OF THE BAR.
  //
  // MobileTabBar sets `z-index: 900`, which makes it a stacking context: no
  // descendant can paint above it, whatever z-index it asks for. The chat
  // overlay sits at 1450, so opening notifications from inside a conversation
  // put the panel behind the conversation — it looked like the tab did
  // nothing. Rendering into <body> takes it out of the bar's context entirely.
  //
  // The desktop dropdown is NOT portalled: it is positioned against the button
  // it belongs to, and moving it to <body> would strand it in the corner.
  const renderPanel = (node) => (tab ? createPortal(node, document.body) : node);

  return (
    <div className={`nb${tab ? ' nb--tab' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={tab ? 'nb-tabbtn' : 'nb-btn'}
        onClick={toggle}
        aria-label={label || t('notif_title')}
        title={label || t('notif_title')}
        aria-expanded={open}
      >
        <span className={tab ? 'nb-tabicon' : 'nb-icon'}>
          <svg width={tab ? 22 : 18} height={tab ? 22 : 18} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            {isDesigner
              ? <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
              : (
                <>
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.7 21a2 2 0 0 1-3.4 0" />
                </>
              )}
          </svg>
          {unread.length > 0 && (
            <span className={tab ? 'mtb-badge' : 'nb-badge'}>
              {unread.length > 9 ? '9+' : unread.length}
            </span>
          )}
        </span>
      </button>

      {open && renderPanel(
        <div className={`nb-panel${tab ? ' nb-panel--sheet' : ''}`} role="menu" ref={panelRef}>
          <div className="nb-head">
            {t('notif_title')}
            {/* The sheet covers the screen on a phone, so it needs its own way
                out — there is no visible backdrop to tap. */}
            {tab && (
              <button type="button" className="nb-x" onClick={() => setOpen(false)}
                aria-label={t('cancel')}>✕</button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="nb-empty">{t('notif_none')}</p>
          ) : (
            <ul className="nb-list">
              {items.map(n => {
                const to = destinationOf(n, user?.uid);
                const body = (
                  <>
                    <span className="nb-avatar">
                      {n.fromAvatar
                        ? <img src={n.fromAvatar} alt="" />
                        : <PersonIcon />}
                    </span>
                    <span className="nb-text">
                      {messageOf(n, t)}
                      {n.itemTitle && <em className="nb-item-title">{n.itemTitle}</em>}
                    </span>
                  </>
                );
                const cls = `nb-item${n.read ? '' : ' nb-item--unread'}${to ? '' : ' nb-item--plain'}`;
                return (
                  <li key={n.id}>
                    {to ? (
                      // Closes the CONVERSATION overlay as well as this panel.
                      // Messages are state, not a route, and the provider drops
                      // them on a change of PATHNAME — so a notification that
                      // leads to the page already underneath (a reply on a
                      // photo of the designer you are looking at) changes only
                      // the query string, and the overlay stayed put on top of
                      // the thing it had just opened. Tapping a notification
                      // means "take me to that", which always means leaving
                      // messages, so it is said here rather than inferred.
                      <Link to={to} className={cls}
                        onClick={() => { setOpen(false); closeMessages(); }}>
                        {body}
                      </Link>
                    ) : (
                      <div className={cls}>{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>,
      )}
    </div>
  );
}
